import "./cabBooking.css";

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAppToast } from "./AppToastProvider";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";

const DRIVER_POOL = [
  { name: "Ravi Kumar", car: "Swift Dzire · Sedan", rating: 4.8, etaMinutes: 4, distanceKm: 1.2 },
  { name: "Anita Sharma", car: "Wagon R · Hatchback", rating: 4.6, etaMinutes: 6, distanceKm: 2.1 },
  { name: "Mohammed Irfan", car: "Ertiga · SUV", rating: 4.9, etaMinutes: 5, distanceKm: 1.8 },
  { name: "Suresh Patil", car: "Etios · Sedan", rating: 4.5, etaMinutes: 8, distanceKm: 3.4 },
  { name: "Priya Nair", car: "i20 · Hatchback", rating: 4.7, etaMinutes: 7, distanceKm: 2.6 }
];

const buildDrivers = (minFare) =>
  DRIVER_POOL.map((driver, index) => ({
    ...driver,
    id: `${driver.name}-${index}`,
    quotedFare: Math.max(Number(minFare) || 0, 0) + index * 15 + 20
  }));

const CabBookingScreen = () => {
  const navigate = useNavigate();
  const toast = useAppToast();

  const [step, setStep] = useState("form");
  const [pickup, setPickup] = useState("Current Location (auto-detected)");
  const [destination, setDestination] = useState("");
  const [minFare, setMinFare] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const isFormValid = useMemo(
    () => destination.trim().length > 2 && Number(minFare) > 0,
    [destination, minFare]
  );

  const findDrivers = (event) => {
    event.preventDefault();

    if (!isFormValid) {
      toast.showInfo("Enter a destination and your minimum fare offer.");
      return;
    }

    setDrivers(buildDrivers(minFare));
    setSelectedDriver(null);
    setStep("drivers");
  };

  const approachDriver = (driver) => {
    setSelectedDriver(driver);
    setStep("requested");
    toast.showSuccess(`Request sent to ${driver.name}. They will contact you shortly.`);
  };

  const resetFlow = () => {
    setStep("form");
    setDestination("");
    setMinFare("");
    setDrivers([]);
    setSelectedDriver(null);
  };

  return (
    <div className="cb-page">
      <SiteHeader />

      <div className="cb-subheader">
        <button type="button" className="cb-back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left" aria-hidden="true"></i> Back
        </button>
        <div>
          <p className="cb-eyebrow">Priority One</p>
          <h1>Cab Booking</h1>
        </div>
      </div>

      <main className="cb-main">
        <section className="cb-map-card">
          <div className="cb-map-grid" aria-hidden="true">
            <div className="cb-map-route" />
            <div className="cb-map-pin cb-map-pin-start">
              <span />
              <small>Pickup</small>
            </div>
            <div className="cb-map-pin cb-map-pin-end">
              <span />
              <small>Destination</small>
            </div>
          </div>
          <div className="cb-map-caption">
            <p>{pickup}</p>
            <i className="fas fa-arrow-right" aria-hidden="true"></i>
            <p>{destination || "Enter your destination"}</p>
          </div>
        </section>

        <section className="cb-panel">
          <AnimatePresence mode="wait">
            {step === "form" && (
              <motion.form
                key="form"
                className="cb-form"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.28 }}
                onSubmit={findDrivers}
              >
                <h2>Where are you headed?</h2>
                <p className="cb-panel-copy">
                  Enter your destination and the minimum fare you are willing to pay. We will share your offer
                  with nearby drivers so you can pick who to approach.
                </p>

                <label>
                  <span>Pickup location</span>
                  <input type="text" value={pickup} onChange={(event) => setPickup(event.target.value)} />
                </label>

                <label>
                  <span>Destination</span>
                  <input
                    type="text"
                    placeholder="Enter drop location"
                    value={destination}
                    onChange={(event) => setDestination(event.target.value)}
                  />
                </label>

                <label>
                  <span>Your minimum fare offer (₹)</span>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 150"
                    value={minFare}
                    onChange={(event) => setMinFare(event.target.value)}
                  />
                </label>

                <button type="submit" className="cb-primary-btn">
                  Send Offer To Nearby Drivers
                </button>
              </motion.form>
            )}

            {step === "drivers" && (
              <motion.div
                key="drivers"
                className="cb-driver-list"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.28 }}
              >
                <div className="cb-driver-head">
                  <div>
                    <h2>Choose a driver to approach</h2>
                    <p className="cb-panel-copy">
                      Your offer of ₹{minFare} for {pickup} → {destination} has been shared with these drivers.
                      Select one to send your request.
                    </p>
                  </div>
                  <button type="button" className="cb-text-btn" onClick={() => setStep("form")}>
                    Edit request
                  </button>
                </div>

                <div className="cb-driver-grid">
                  {drivers.map((driver, index) => (
                    <motion.article
                      key={driver.id}
                      className="cb-driver-card"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.22, delay: index * 0.05 }}
                    >
                      <div className="cb-driver-top">
                        <h3>{driver.name}</h3>
                        <span>★ {driver.rating}</span>
                      </div>
                      <p className="cb-driver-car">{driver.car}</p>
                      <div className="cb-driver-meta">
                        <span>{driver.distanceKm} km away</span>
                        <span>{driver.etaMinutes} min ETA</span>
                        <span>Usual fare ₹{driver.quotedFare}</span>
                      </div>
                      <button type="button" className="cb-primary-btn" onClick={() => approachDriver(driver)}>
                        Approach This Driver
                      </button>
                    </motion.article>
                  ))}
                </div>
              </motion.div>
            )}

            {step === "requested" && selectedDriver && (
              <motion.div
                key="requested"
                className="cb-confirmation"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="cb-confirmation-icon">
                  <i className="fas fa-check" aria-hidden="true"></i>
                </div>
                <h2>Request sent to {selectedDriver.name}</h2>
                <p className="cb-panel-copy">
                  Your offer of ₹{minFare} for {pickup} → {destination} has been shared with {selectedDriver.name}.
                  They will contact you on your registered number shortly.
                </p>
                <button type="button" className="cb-primary-btn" onClick={resetFlow}>
                  Book Another Ride
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default CabBookingScreen;
